import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Tag, Button, Spin, Alert, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconMailCheck, IconMailX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { message } from '../../../services/toastMessage';
import AdminResponsiveList, { IAdminCardModel } from '../../../components/Admin/AdminResponsiveList';
import { getSignupLeads, setSignupLeadContacted } from '../../../services/SiteModeService';
import { ISignupLead } from '../../../interfaces/heavyDutyMachinery/ISiteMode';
import { SIGNUP_LEAD_INTEREST_LABEL_KEYS } from '../../../constants/signupLeadInterests';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';

const { Title, Text, Paragraph } = Typography;

/**
 * Who has left their e-mail on the pre-launch page.
 *
 * The one thing this screen has to be honest about is written across the
 * top of it: nothing here is verified. There is no confirmation e-mail yet
 * -- the platform cannot send one -- so every row is a string somebody
 * typed, and a list of "interested customers" that quietly includes three
 * addresses a bot invented is worse than no list at all.
 *
 * "Contactado" is a tick the admin keeps for themselves, not something the
 * person on the other end ever sees. It is the difference between a list of
 * fifty and a list of the eleven nobody has written to yet.
 */
const SignupLeadsSection: React.FC = () => {
  const { t } = useTranslation();
  const [leads, setLeads] = useState<ISignupLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);

  useEffect(() => {
    getSignupLeads()
      .then(setLeads)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const handleToggleContacted = async (lead: ISignupLead) => {
    setSavingId(lead.id);
    try {
      const updated = await setSignupLeadContacted(lead.id, !lead.contacted);
      setLeads((prev) => prev.map((row) => (row.id === lead.id ? updated : row)));
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    } finally {
      setSavingId(null);
    }
  };

  const interestTags = (interests: string[]) => (
    <Space size={4} wrap>
      {interests.map((interest) => (
        <Tag key={interest} style={{ marginInlineEnd: 0, borderRadius: 999 }}>
          {/* An interest the server knows about but this build does not
              prints as itself rather than as a blank tag. */}
          {SIGNUP_LEAD_INTEREST_LABEL_KEYS[interest] ? translate(t, SIGNUP_LEAD_INTEREST_LABEL_KEYS[interest]) : interest}
        </Tag>
      ))}
    </Space>
  );

  const contactedButton = (lead: ISignupLead) => (
    <Tooltip
      title={translate(
        t,
        lead.contacted ? TRANSLATIONS.ADMIN_SIGNUP_LEADS_MARK_PENDING : TRANSLATIONS.ADMIN_SIGNUP_LEADS_MARK_CONTACTED,
      )}
    >
      <Button
        size="small"
        loading={savingId === lead.id}
        icon={lead.contacted ? <IconMailX size={15} /> : <IconMailCheck size={15} />}
        onClick={() => handleToggleContacted(lead)}
      >
        {translate(
          t,
          lead.contacted ? TRANSLATIONS.ADMIN_SIGNUP_LEADS_MARK_PENDING : TRANSLATIONS.ADMIN_SIGNUP_LEADS_MARK_CONTACTED,
        )}
      </Button>
    </Tooltip>
  );

  const columns = useMemo(
    () => [
      {
        title: translate(t, TRANSLATIONS.ADMIN_SIGNUP_LEADS_COLUMN_EMAIL),
        dataIndex: 'email',
        key: 'email',
        render: (email: string) => <a href={`mailto:${email}`}>{email}</a>,
      },
      {
        title: translate(t, TRANSLATIONS.ADMIN_SIGNUP_LEADS_COLUMN_PHONE),
        dataIndex: 'phone',
        key: 'phone',
        render: (phone?: string) => (phone ? <a href={`tel:${phone}`}>{phone}</a> : <Text type="secondary">—</Text>),
      },
      {
        title: translate(t, TRANSLATIONS.ADMIN_SIGNUP_LEADS_COLUMN_INTERESTS),
        dataIndex: 'interests',
        key: 'interests',
        render: (interests: string[]) => interestTags(interests),
      },
      {
        title: translate(t, TRANSLATIONS.ADMIN_SIGNUP_LEADS_COLUMN_MESSAGE),
        dataIndex: 'message',
        key: 'message',
        render: (text?: string) =>
          text ? (
            <Text style={{ fontSize: 13 }}>{text}</Text>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: translate(t, TRANSLATIONS.ADMIN_SIGNUP_LEADS_COLUMN_CREATED),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (createdAt: string) => dayjs(createdAt).format('DD/MM/YYYY HH:mm'),
        sorter: (a: ISignupLead, b: ISignupLead) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
      },
      {
        title: translate(t, TRANSLATIONS.ADMIN_SIGNUP_LEADS_COLUMN_CONTACTED),
        key: 'contacted',
        render: (_: unknown, lead: ISignupLead) => (
          <Space>
            <Tag color={lead.contacted ? 'green' : 'default'} style={{ borderRadius: 999 }}>
              {translate(
                t,
                lead.contacted ? TRANSLATIONS.ADMIN_SIGNUP_LEADS_CONTACTED_YES : TRANSLATIONS.ADMIN_SIGNUP_LEADS_CONTACTED_NO,
              )}
            </Tag>
            {contactedButton(lead)}
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, savingId, leads],
  );

  const toCard = (lead: ISignupLead): IAdminCardModel => ({
    key: String(lead.id),
    title: <a href={`mailto:${lead.email}`}>{lead.email}</a>,
    subtitle: lead.phone ? <a href={`tel:${lead.phone}`}>{lead.phone}</a> : undefined,
    badge: (
      <Tag color={lead.contacted ? 'green' : 'default'} style={{ marginInlineEnd: 0, borderRadius: 999 }}>
        {translate(
          t,
          lead.contacted ? TRANSLATIONS.ADMIN_SIGNUP_LEADS_CONTACTED_YES : TRANSLATIONS.ADMIN_SIGNUP_LEADS_CONTACTED_NO,
        )}
      </Tag>
    ),
    fields: [
      { label: translate(t, TRANSLATIONS.ADMIN_SIGNUP_LEADS_COLUMN_INTERESTS), value: interestTags(lead.interests), wide: true },
      { label: translate(t, TRANSLATIONS.ADMIN_SIGNUP_LEADS_COLUMN_MESSAGE), value: lead.message, wide: true },
      { label: translate(t, TRANSLATIONS.ADMIN_SIGNUP_LEADS_COLUMN_CREATED), value: dayjs(lead.createdAt).format('DD/MM/YYYY HH:mm') },
    ],
    actions: contactedButton(lead),
  });

  return (
    <div>
      <Title level={4} style={{ marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_SIGNUP_LEADS)}
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        {translate(t, TRANSLATIONS.ADMIN_SIGNUP_LEADS_DESCRIPTION)}
      </Text>

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16, borderRadius: 12 }}
        message={translate(t, TRANSLATIONS.ADMIN_SIGNUP_LEADS_UNVERIFIED_NOTICE)}
      />

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_SIGNUP_LEADS_LOAD_ERROR)} />
      ) : leads.length === 0 ? (
        <Paragraph type="secondary">{translate(t, TRANSLATIONS.ADMIN_SIGNUP_LEADS_EMPTY)}</Paragraph>
      ) : (
        <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 10, fontSize: 13 }}>
            {translate(t, TRANSLATIONS.ADMIN_SIGNUP_LEADS_TOTAL, { count: leads.length })}
          </Text>
          <AdminResponsiveList<ISignupLead>
            dataSource={leads}
            rowKey="id"
            columns={columns}
            toCard={toCard}
            pagination={{ pageSize: 20, showSizeChanger: false }}
          />
        </>
      )}
    </div>
  );
};

export default SignupLeadsSection;
